import { createORPCClient } from "@orpc/client";
import { RPCLink } from "@orpc/client/fetch";
import { createTanstackQueryUtils } from "@orpc/tanstack-query";

import type { AppRouterClient } from "@garimpa/api/appRouter";

/**
 * Isomorphic RPC client.
 *
 * - **Server**: `globalThis.$client` is installed explicitly by
 *   `initializeServerRpcClient.server.ts` during viewer-context bootstrap.
 *   Loaders invoke the router in-process — no HTTP roundtrip.
 * - **Browser**: falls through to an `RPCLink` pointed at `/api/rpc`.
 *
 * The Proxy below resolves the underlying client at **call time**, not at
 * module-load time. This is essential on the server: `orpc.client.ts` loads
 * during server startup, before any request fires — at that point
 * `globalThis.$client` is still undefined. Without the lazy lookup, `client`
 * would be permanently bound to the browser fallback, and every server call
 * would trip the RPCLink guard below.
 *
 * On the browser, the Proxy still works — `globalThis.$client` stays
 * `undefined` and we consistently use the RPCLink client.
 *
 * The RPCLink's `url()` throws if reached on the server — canary for the
 * case where server bootstrap forgets to initialize the in-process client.
 * The previous self-fetch pattern (`${process.env.APP_URL}/api/rpc`) caused
 * request timeouts because the outbound fetch went out to the public edge.
 */
declare global {
  // biome-ignore lint/style/noVar: globalThis augmentation requires var
  var $client: AppRouterClient | undefined;
}

const browserLink = new RPCLink({
  url: () => {
    if (typeof window === "undefined") {
      throw new Error(
        "oRPC RPCLink was invoked on the server. initializeServerRpcClient() must run " +
          "before the first SSR call.",
      );
    }
    return `${window.location.origin}/api/rpc`;
  },
  fetch(url, options) {
    return fetch(url, {
      ...options,
      credentials: "include",
    });
  },
});

const browserClient = createORPCClient<AppRouterClient>(browserLink);

export const client: AppRouterClient = new Proxy({} as AppRouterClient, {
  get(_target, prop, receiver) {
    const current = (globalThis.$client ?? browserClient) as object;
    return Reflect.get(current, prop, receiver);
  },
});

export const orpc = createTanstackQueryUtils(client);
